import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getCrmErrorMessage,
  getCrmReference,
  updateCrmTask,
} from '@/services/crmService'
import { useCRMStore } from '@/store/crm/crmStore'

export const crmQueryKeys = {
  all: (accountId: string | number) => ['crm', String(accountId)] as const,
  reference: (accountId: string | number) =>
    ['crm', String(accountId), 'reference'] as const,
  tasks: (accountId: string | number) =>
    ['crm', String(accountId), 'tasks'] as const,
  task: (accountId: string | number, taskId: number) =>
    ['crm', String(accountId), 'task', taskId] as const,
}

/** Site id from the route, falling back to the stored account like other pages. */
export const useCrmAccountId = (): string | null => {
  const { siteId } = useParams()
  return siteId ?? localStorage.getItem('accountId')
}

/** CRM reference lists (statuses, types, assignees, priorities), cached per account. */
export const useCrmReference = (accountId: string | number | null) =>
  useQuery({
    queryKey: crmQueryKeys.reference(accountId ?? ''),
    queryFn: () => getCrmReference(accountId as string | number),
    enabled: accountId != null && accountId !== '',
    staleTime: 5 * 60 * 1000,
    // A site without the CRM schema answers 500 every time; retrying only delays the dialog's notice.
    retry: false,
  })

/** Refreshes every task list (all sections) and open task details of the account. */
export const useRefreshCrmTasks = (accountId: string | number | null) => {
  const queryClient = useQueryClient()
  return useCallback(() => {
    if (accountId == null) return Promise.resolve()
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.tasks(accountId),
      }),
      queryClient.invalidateQueries({
        queryKey: ['crm', String(accountId), 'task'],
      }),
    ]).then(() => undefined)
  }, [accountId, queryClient])
}

/** Moves a task to another status through the task update route, with a toast for the outcome. */
export const useChangeCrmTaskStatus = (accountId: string | number | null) => {
  const refreshTasks = useRefreshCrmTasks(accountId)
  const showToast = useCRMStore(state => state.showToast)
  return useCallback(
    async (taskId: number, statusId: number) => {
      if (accountId == null) return
      try {
        await updateCrmTask(accountId, taskId, { statusId })
        showToast('הסטטוס עודכן')
        await refreshTasks()
      } catch (error) {
        showToast(await getCrmErrorMessage(error, 'עדכון הסטטוס נכשל'), 'error')
      }
    },
    [accountId, refreshTasks, showToast]
  )
}
